import useDocumentMeta from '../hooks/useDocumentMeta.js';

export default function PrivacyPolicy() {
  useDocumentMeta({
    title: 'Privacy Policy | Account Editor',
    description:
      'Learn how Account Editor collects, uses, and protects merchant and customer information when you use our Shopify app and website.',
    path: '/privacy'
  });

  return (
    <section className="terms">
      <div className="container">
        <header className="terms__hero">
          <span className="eyebrow">
            Legal
          </span>
          <h1 className="terms__title">Privacy policy</h1>
          <p className="terms__lede">
            Protecting your data: our commitment to privacy and security practices.
          </p>
          <span className="terms__updated">Last updated: November 12, 2024</span>
        </header>

        <article className="terms__article">
          <p className="terms__intro">
            Thank you for choosing Account Editor. We respect your privacy and are committed to
            protecting your personal information. This Privacy Policy explains how we collect,
            use, and disclose information when you install and use our app and website.
          </p>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">01</span>
              Introduction
            </h2>
            <p className="terms__p">
              Account Editor ("we," "us," or "our") is a Shopify app designed to empower merchants
              by allowing their customers to edit orders post-purchase. This policy outlines how
              we handle data collected from merchants and their customers.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">02</span>
              Information we collect
            </h2>

            <h3 className="terms__h3">2.1 Merchant information</h3>
            <p className="terms__p">
              When you install Account Editor, we collect the following information from your
              Shopify account:
            </p>
            <ul className="terms__list">
              <li><strong>Store Information:</strong> Store name, contact details, and store URL.</li>
              <li><strong>Billing Information:</strong> To process payments for subscription plans.</li>
              <li><strong>Order Data:</strong> Access to order information to enable editing and cancellation features.</li>
              <li><strong>Product Data:</strong> Details about products to allow modifications by customers.</li>
              <li><strong>Customer Data:</strong> Access to customer information associated with orders.</li>
            </ul>

            <h3 className="terms__h3">2.2 Customer information</h3>
            <p className="terms__p">
              Through our app's functionality on your store, we may process the following customer
              data:
            </p>
            <ul className="terms__list">
              <li><strong>Contact Information:</strong> Name, email address, phone number.</li>
              <li><strong>Order Details:</strong> Products purchased, quantities, prices.</li>
              <li><strong>Shipping Information:</strong> Shipping address and delivery preferences.</li>
              <li><strong>Modification Requests:</strong> Any changes customers make to their orders.</li>
            </ul>

            <h3 className="terms__h3">2.3 Automatically collected information</h3>
            <p className="terms__p">
              When you or your customers interact with our app or website, we may automatically
              collect:
            </p>
            <ul className="terms__list">
              <li><strong>Log Data:</strong> IP addresses, browser type, operating system.</li>
              <li><strong>Usage Data:</strong> Pages viewed, actions taken within the app, timestamps.</li>
            </ul>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">03</span>
              How we use the information
            </h2>
            <p className="terms__p">We use the collected information to:</p>
            <ul className="terms__list">
              <li><strong>Provide and Improve Services:</strong> Enable order editing and cancellation features.</li>
              <li><strong>Process Transactions:</strong> Handle billing and payments for subscription plans.</li>
              <li><strong>Communicate:</strong> Send updates, notifications, and respond to inquiries.</li>
              <li><strong>Analytics:</strong> Analyze usage to improve app performance and user experience.</li>
              <li><strong>Compliance:</strong> Ensure adherence to legal obligations and Shopify policies.</li>
            </ul>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">04</span>
              How we share the information
            </h2>
            <p className="terms__p">
              We do not sell or rent your personal information to third parties. We may share
              information in the following circumstances:
            </p>
            <ul className="terms__list">
              <li><strong>Service Providers:</strong> With third-party vendors who assist in providing our services (e.g., payment processors, hosting services).</li>
              <li><strong>Legal Requirements:</strong> If required by law, regulation, or legal process.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets.</li>
            </ul>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">05</span>
              Data retention
            </h2>
            <p className="terms__p">
              We retain personal information only as long as necessary to fulfill the purposes
              outlined in this policy:
            </p>
            <ul className="terms__list">
              <li><strong>Merchant Data:</strong> Retained while your account is active or as needed to provide services.</li>
              <li><strong>Customer Data:</strong> Retained according to your instructions and as required to provide services.</li>
              <li><strong>Legal Obligations:</strong> Retained as necessary to comply with legal obligations.</li>
            </ul>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">06</span>
              Security
            </h2>
            <p className="terms__p">
              We implement reasonable security measures to protect personal information against
              unauthorized access, alteration, disclosure, or destruction. However, no method of
              transmission over the internet is entirely secure.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">07</span>
              Your rights
            </h2>

            <h3 className="terms__h3">7.1 Merchants</h3>
            <p className="terms__p">You have the right to:</p>
            <ul className="terms__list">
              <li><strong>Access and Update:</strong> Review and update your personal information.</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a structured format.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal obligations.</li>
            </ul>

            <h3 className="terms__h3">7.2 Customers</h3>
            <p className="terms__p">
              As a merchant's customer, you should contact the merchant directly to exercise your
              rights regarding personal information.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">08</span>
              Third-party services
            </h2>
            <p className="terms__p">
              Our app may contain links to third-party websites or services. We are not responsible
              for the privacy practices of these third parties. We recommend reviewing their
              privacy policies.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">09</span>
              International data transfers
            </h2>
            <p className="terms__p">
              If you are accessing our services from outside United States, your information may be
              transferred to and processed in [Your Country/Region] where our servers are located.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">10</span>
              Children's privacy
            </h2>
            <p className="terms__p">
              Our services are not intended for individuals under 13 years of age. We do not
              knowingly collect personal information from children.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">11</span>
              Changes to this privacy policy
            </h2>
            <p className="terms__p">
              We may update this Privacy Policy from time to time. We will notify you of any
              significant changes by posting the new policy on this page with an updated revision
              date.
            </p>
          </section>

          <section className="terms__section">
            <h2 className="terms__h2">
              <span className="terms__h2-num">12</span>
              Contact us
            </h2>
            <p className="terms__p">
              If you have any questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <div className="terms__contact">
              <span className="terms__contact-label">Email</span>
              <a href="mailto:support@accounteditor.com">
                <i className="ph ph-envelope-simple" aria-hidden="true"></i>
                support@accounteditor.com
              </a>
            </div>
          </section>
        </article>
      </div>
    </section>
  );
}
